//#region Imports
import * as React from 'react';
import { useState, useEffect } from 'react';
import { Stage, Layer, Rect, Group, Text } from 'react-konva';
import { Puc } from '../models/puc';
import { RenderItem } from '../viewModels/renderItem';
import { Locker } from '../models/locker';
import { Board } from '../models/board';
import { Screen } from '../models/screen';
//#endregion

//#region Custom Props
interface CabinetRendererProps {
  pucs: Array<Puc>,
  canvasWidth: number,
  canvasHeight: number,
  hardwareChanged: number,
  onPucSelected?: (pucId: string) => void,
  onBoardSelected?: (boardId: string) => void,
  onScreenSelected?: (screenId: string) => void,
  onLockerSelected?: (lockerId: string) => void,
  enableInput: boolean
}
//#endregion

function CabinetRenderer({
  pucs,
  canvasWidth,
  canvasHeight,
  onPucSelected,
  onBoardSelected,
  onScreenSelected,
  onLockerSelected,
  hardwareChanged,
  enableInput
}: CabinetRendererProps) {
  //#region Hardware Model state
  const [lockers, setLockers] = useState<Array<Locker>>([]);
  //#endregion

  //#region Render state members
  const [pucToCanvasScale, setPucToCanvasScale] = useState<number>(1);
  const [renderLockers, setRenderLockers] = useState<Array<RenderItem>>([]);
  const [renderLockerLabels, setRenderLockerLabels] = useState<Array<RenderItem>>([]);
  const [renderScreens, setRenderScreens] = useState<Array<RenderItem>>([]);
  const [renderPucs, setRenderPucs] = useState<Array<RenderItem>>([]);
  //#endregion

  //#region Selection members
  const [selectedPucId, setSelectedPucId] = useState<string>('');
  const [selectedScreenId, setSelectedScreenId] = useState<string>('');
  const [selectedBoardId, setSelectedBoardId] = useState<string>('');
  const [selectedLockerId, setSelectedLockerId] = useState<string>('');
  //#endregion

  //#region Utility Methods
  const resetLockerColors = () => {
    for (const renderLocker of renderLockers) {
      renderLocker.fill = '#77AC1D';
    }
  };
  //#endregion
  
  //#region Cabinet Rendering
  const totalLength = (dimension: string) => {
    if (dimension === 'width') {
      const minX = Math.min.apply(null, pucs.map(p => p.origin_x_cm));
      const maxX = Math.max.apply(null, pucs.map(p => p.origin_x_cm + p.width_cm));
      return (maxX - minX);
    } else {
      const minY = Math.min.apply(null, pucs.map(p => p.origin_y_cm));
      const maxY = Math.max.apply(null, pucs.map(p => p.origin_y_cm + p.height_cm));
      return (maxY - minY);
    }
  };

  const centerX = () => {
    const pucCenter = ((totalLength('width') / 2) * pucToCanvasScale);
    return canvasWidth / 2 + (-pucCenter);
  };
  //#endregion

  //#region Input Handling
  const onPucClicked = (pucId: string) => {
    onPucSelected?.(pucId);
    if (!enableInput) {
      return;
    }

    resetLockerColors();
    setSelectedPucId(pucId);
    setSelectedLockerId('');
    setSelectedBoardId('');
    setSelectedScreenId('');
  };

  const onBoardClicked = (boardId: string) => {
    onBoardSelected?.(boardId);

    if (!enableInput) {
      return;
    }
    setSelectedBoardId(boardId);
    
    const boardLockers = lockers.filter(x => x.board_id === boardId);
    for (const renderLocker of renderLockers) {
      const matchingLocker = boardLockers.find(x => x.id === renderLocker.id);
      if (matchingLocker) {
        renderLocker.fill = 'red';
      } else {
        renderLocker.fill = '#77AC1D';
      }
    }

    setSelectedPucId('');
    setSelectedLockerId('');
    setSelectedScreenId('');
  };

  const onLockerClicked = (lockerId: string) => {
    onLockerSelected?.(lockerId);
    if (!enableInput) {
      return;
    }
    resetLockerColors();
    const locker = lockers.find(x => x.id === lockerId);
    setSelectedLockerId(locker!.id);
    
    const renderLocker = renderLockers.find(x => x.id === locker!.id);
    if (renderLocker) {
      renderLocker!.fill = 'red';
    }
    
    setSelectedPucId('');
    setSelectedBoardId('');
    setSelectedScreenId('');
  };

  const onScreenClicked = (screenId: string) => {
    onScreenSelected?.(screenId);
    if (!enableInput) {
      return;
    }
    setSelectedScreenId(screenId);

    setSelectedLockerId('');
    setSelectedPucId('');
    setSelectedBoardId('');
  };
  //#endregion

  useEffect(() => {
    //#region Reset
    const totalHeight = totalLength('height');
    const totalWidth = totalLength('width');
    const pucToCanvasScale = Math.min(canvasHeight / totalHeight, canvasWidth / totalWidth);
    setPucToCanvasScale(pucToCanvasScale);
    
    setRenderPucs([]);
    setRenderScreens([]);
    setRenderLockers([]);
    setRenderLockerLabels([]);
    setLockers([]);
    //#endregion

    //#region Build render items
    const tempRenderPuc: Array<RenderItem> = [];
    const tempRenderScreen: Array<RenderItem> = [];
    const tempRenderLocker: Array<RenderItem> = [];
    const tempRenderLabel: Array<RenderItem> = [];
    const tempLockers: Array<Locker> = [];
    const tempBoards: Array<Board> = [];
    const tempScreens: Array<Screen> = [];

    for (const puc of pucs) {
      const offsetX = puc.origin_x_cm || 0;
      const offsetY = puc.origin_y_cm || 0;
      for (const screen of puc.screens || []) {
        tempScreens.push(screen);
        tempRenderScreen.push(new RenderItem(
          screen.id.toString(),
          screen.origin_x_cm + offsetX,
          screen.origin_y_cm + offsetY,
          screen.width_cm,
          screen.height_cm, '', '', 'white', 'black'));
      }
      
      const pucRender = new RenderItem(
        puc.id,
        offsetX * pucToCanvasScale,
        offsetY * pucToCanvasScale,
        puc.width_cm,
        puc.height_cm, '', '', '#d2d3d5', 'black');
      
      tempRenderPuc.push(pucRender);
      for (const board of puc.boards) {
        tempBoards.push(board);
        for (const locker of board.lockers) {
          tempLockers.push(locker);
          const fill = '#77AC1D';
          const lockerX = locker.origin_x_cm + offsetX;
          const lockerY = locker.origin_y_cm + offsetY;
          const lockerWidth = locker.width_cm;
          const lockerHeight = locker.height_cm;
          const renderLocker = new RenderItem(
            locker.id,
            lockerX,
            lockerY,
            lockerWidth,
            lockerHeight,
            locker.label,
            'white',
            fill,
            '#444444'
          );
          tempRenderLocker.push(renderLocker);

          const renderLockerLabel = new RenderItem(
            locker.id,
            lockerX + 0,
            lockerY + lockerHeight,
            lockerWidth,
            lockerHeight,
            locker.label,
            'white',
            'blue',
            '#444444'
          );
          tempRenderLabel.push(renderLockerLabel);
        }
      }
    }
    //#endregion

    setRenderPucs(tempRenderPuc);
    setRenderScreens(tempRenderScreen);
    setRenderLockers(tempRenderLocker);
    setRenderLockerLabels(tempRenderLabel);
    setLockers(tempLockers);
    
    if (enableInput) {
      if (selectedScreenId) {
        onScreenClicked(selectedScreenId);
      } else if (selectedLockerId) {
        onLockerClicked(selectedLockerId);
      } else if (selectedBoardId) {
        onBoardClicked(selectedBoardId);
      } else if (selectedPucId) {
        onPucClicked(selectedPucId);
      } else if (pucs.length > 0 && pucs[0].id) {
        onPucClicked(pucs[0].id);
      }
    }

  }, [hardwareChanged]);

  return (
    <div className=" text-center text-dark">
      <Stage
        width={canvasWidth}
        height={canvasHeight}>
        <Layer
          scaleX={1}
          x={centerX()}
          scaleY={-1}
          y={canvasHeight}>
          {renderPucs.map((renderPuc, renderPucIndex) => (
            <Rect
              key={renderPucIndex}
              stroke={renderPuc.stroke}
              fill={renderPuc.fill}
              strokeWidth={1}
              scaleX={pucToCanvasScale}
              scaleY={pucToCanvasScale}
              width={renderPuc.width}
              height={renderPuc.height}
              x={renderPuc.x}
              y={renderPuc.y}
              onClick={() => {
                onPucClicked(renderPuc.id);
              }} />
          ))}
          {renderLockers.map((renderLocker, renderItemIndex) => (
            <Group
              key={renderItemIndex}
              x={0}
              scaleX={pucToCanvasScale}
              scaleY={pucToCanvasScale}>
              <Rect
                x={renderLocker.x}
                y={renderLocker.y}
                width={renderLocker.width}
                height={renderLocker.height}
                fill={renderLocker.fill}
                stroke={'black'}
                strokeWidth={1}
                onClick={() => {
                  onLockerClicked(renderLocker.id);
                }} />
              <Text
                x={renderLockerLabels[renderItemIndex].x}
                y={renderLockerLabels[renderItemIndex].y}
                width={renderLockerLabels[renderItemIndex].width}
                text={renderLockerLabels[renderItemIndex].label}
                fontSize={16}
                fill={renderLockerLabels[renderItemIndex].labelColor}
                scaleX={1}
                scaleY={-1}
                align={'center'}
                verticalAlign={'center'}
                onClick={() => {
                  onLockerClicked(renderLocker.id);
                }} />
            </Group>
          ))}
          {renderScreens.map((renderScreen, renderScreenIndex) => (
            <Group
              key={renderScreenIndex}
              x={0}
              scaleX={pucToCanvasScale}
              scaleY={pucToCanvasScale}>
              <Rect
                x={renderScreen.x}
                y={renderScreen.y}
                width={renderScreen.width}
                height={renderScreen.height}
                fill={renderScreen.fill}
                stroke={renderScreen.stroke}
                strokeWidth={1}
                onClick={() => {
                  onScreenClicked(renderScreen.id);
                }} />
            </Group>
          ))}
        </Layer>
      </Stage>
    </div>
  );
}

export default CabinetRenderer;
